import React, { useState } from 'react';
import './ComplianceScannerReport.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Single source for traffic-light dot styling
const StatusDot = ({ status }) => {
  const cls = status === 'red' ? 'cs-dot-red'
            : status === 'amber' ? 'cs-dot-amber'
            : 'cs-dot-green';
  return <span className={`cs-status-dot ${cls}`} />;
};

// Counts pill row at the top of the report
const CountsRow = ({ summary }) => (
  <div className="cs-counts" data-testid="cs-counts">
    <span className="cs-count-pill cs-count-red"
          data-testid="cs-count-red">
      🔴 {summary.red || 0} critical
    </span>
    <span className="cs-count-pill cs-count-amber"
          data-testid="cs-count-amber">
      🟠 {summary.amber || 0} warnings
    </span>
    <span className="cs-count-pill cs-count-green"
          data-testid="cs-count-green">
      🟢 {summary.green || 0} compliant
    </span>
  </div>
);

const ReasonModal = ({ open, title, onClose, onSubmit, submitLabel }) => {
  const [reason, setReason] = useState('');
  if (!open) return null;
  return (
    <div className="cs-modal-backdrop" data-testid="cs-reason-modal">
      <div className="cs-modal">
        <h3>{title}</h3>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Please provide a reason — this is recorded with timestamp and your account ID for audit purposes."
          data-testid="cs-reason-input"
        />
        <div className="cs-modal-actions">
          <button className="cs-btn cs-btn-ghost"
                  onClick={() => { setReason(''); onClose(); }}
                  data-testid="cs-reason-cancel">Cancel</button>
          <button className="cs-btn cs-btn-primary"
                  disabled={!reason.trim()}
                  onClick={() => { onSubmit(reason.trim()); setReason(''); }}
                  data-testid="cs-reason-submit">{submitLabel}</button>
        </div>
      </div>
    </div>
  );
};

const ItemRow = ({ scanId, item, onRefresh, onActionClick }) => {
  const [modalMode, setModalMode] = useState(null); // 'override' | 'na'

  const handleSubmit = async (reason) => {
    const path = modalMode === 'na'
      ? `not-applicable`
      : `override`;
    try {
      await fetch(
        `${API_BASE_URL}/api/compliance/scanner/${scanId}/items/${item.item_key}/${path}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason }),
        }
      );
      setModalMode(null);
      onRefresh && onRefresh();
    } catch (err) {
      console.error('compliance item action failed', err);
      setModalMode(null);
    }
  };

  const warningCls = item.status === 'amber' ? 'cs-item-warning amber' : 'cs-item-warning';
  const isAcked = !!item.override_acknowledged;
  const isNA = item.status === 'not_applicable';

  return (
    <div className="cs-item" data-testid={`cs-item-${item.item_key}`}>
      <div>
        <div className="cs-item-label">
          <StatusDot status={isNA ? 'green' : item.status} />
          {item.label}
          {item.severity === 'critical' && item.fine_amount && (
            <span className="cs-fine-tag" data-testid={`cs-fine-${item.item_key}`}>
              up to {item.fine_amount}
            </span>
          )}
          {isAcked && (
            <span style={{ marginLeft: 8, fontSize: '0.7rem', color: '#475569' }}>
              · agent acknowledged
            </span>
          )}
          {isNA && (
            <span style={{ marginLeft: 8, fontSize: '0.7rem', color: '#475569' }}>
              · not applicable
            </span>
          )}
        </div>
        <div className="cs-item-details">{item.details}</div>
        {item.legal_warning && !isAcked && !isNA && (
          <div className={warningCls}>{item.legal_warning}</div>
        )}
        {isAcked && item.override_reason && (
          <div className="cs-item-details" style={{ fontStyle: 'italic' }}>
            Override reason: {item.override_reason}
          </div>
        )}
      </div>
      <div className="cs-item-actions">
        {!isAcked && !isNA && item.status !== 'green' && item.action_label && (
          <button className="cs-btn cs-btn-primary"
                  onClick={() => onActionClick && onActionClick(item)}
                  data-testid={`cs-action-${item.item_key}`}>
            {item.action_label}
          </button>
        )}
        {!isNA && item.status !== 'green' && (
          <>
            <button className="cs-btn cs-btn-ghost"
                    onClick={() => setModalMode('override')}
                    data-testid={`cs-override-${item.item_key}`}>
              Acknowledge & Proceed
            </button>
            <button className="cs-btn cs-btn-ghost"
                    onClick={() => setModalMode('na')}
                    data-testid={`cs-na-${item.item_key}`}>
              Mark Not Applicable
            </button>
          </>
        )}
      </div>
      <ReasonModal
        open={modalMode !== null}
        title={modalMode === 'na'
          ? `Mark "${item.label}" as Not Applicable`
          : `Acknowledge risk for "${item.label}"`}
        submitLabel={modalMode === 'na' ? 'Mark Not Applicable' : 'Acknowledge & Proceed'}
        onClose={() => setModalMode(null)}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

const PropertyCard = ({ scan, onRefresh, onActionClick }) => (
  <div className="cs-property-card" data-testid={`cs-property-${scan.property_id}`}>
    <div className="cs-property-head">
      <div className="cs-prop-address">
        <StatusDot status={scan.overall_status} />
        {scan.property_address || '(no address)'}
      </div>
      <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
        Scanned {new Date(scan.triggered_at).toLocaleString()}
      </div>
    </div>
    {scan.items.map((it) => (
      <ItemRow key={it.item_key}
               scanId={scan.id}
               item={it}
               onRefresh={onRefresh}
               onActionClick={onActionClick} />
    ))}
  </div>
);

/**
 * ComplianceScannerReport
 * Props:
 *   - scans:    array of scan objects from the backend
 *   - summary:  { red, amber, green, total_properties }
 *   - onRefresh: () => Promise<void>   — re-fetch scans after override/NA
 *   - onActionClick: (item) => void    — handle "Upload EPC" etc. clicks
 *   - title (optional)
 *   - subtitle (optional)
 */
export default function ComplianceScannerReport({
  scans, summary, onRefresh, onActionClick,
  title = 'Compliance Health Report',
  subtitle,
}) {
  if (!scans || scans.length === 0) {
    return (
      <div className="cs-report" data-testid="cs-report-empty">
        <div className="cs-header">
          <div className="cs-title">{title}</div>
        </div>
        <div className="cs-empty">
          No properties scanned yet. Click <strong>Run Compliance Check</strong> on
          a property to scan it on demand, or finish onboarding to scan your
          whole portfolio.
        </div>
      </div>
    );
  }
  return (
    <div className="cs-report" data-testid="cs-report">
      <div className="cs-header">
        <div>
          <div className="cs-title">{title}</div>
          {subtitle && <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
            {subtitle}
          </div>}
        </div>
        <CountsRow summary={summary || {}} />
      </div>
      {scans.map((s) => (
        <PropertyCard key={s.id} scan={s}
                      onRefresh={onRefresh}
                      onActionClick={onActionClick} />
      ))}
    </div>
  );
}
